import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

export default function SchedulerTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/system/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    } finally {
      setLoading(false);
    }
  };

  const runJob = async (jobId) => {
    setRunningJob(jobId);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/system/scheduler/run-job`, 
        { job_id: jobId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Jobb '${jobId}' kjørt`);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke kjøre jobb');
    } finally {
      setRunningJob(null);
    }
  };

  const jobDescriptions = {
    daily_reminders: {
      name: 'Daglige påminnelser',
      desc: 'Sender påminnelser for oppgaver som forfaller innen 2 dager',
      schedule: 'Hver dag kl 08:00'
    },
    daily_overdue: {
      name: 'Forsinket-varsler',
      desc: 'Sender varsler om forsinkede oppgaver til admins',
      schedule: 'Hver dag kl 09:00'
    },
    weekly_summary: {
      name: 'Ukeoversikt',
      desc: 'Sender ukentlig oppsummering til alle organisasjons-admins',
      schedule: 'Hver mandag kl 08:00'
    },
    cleanup_expired: {
      name: 'Opprydding testkontoer',
      desc: 'Deaktiverer utløpte testbrukere automatisk',
      schedule: 'Hver dag kl 02:00'
    },
    payment_check: {
      name: 'Betalingssjekk',
      desc: 'Sjekker forfalte betalinger og eskalerer automatisk',
      schedule: '1. hver måned kl 10:00'
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div className={`p-4 rounded-lg ${status?.running ? 'bg-green-900/50 border border-green-700' : 'bg-red-900/50 border border-red-700'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${status?.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
          <span className="font-medium">
            Scheduler: {status?.running ? 'Kjører' : 'Stoppet'}
          </span>
        </div>
      </div>

      {/* Job list */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Planlagte jobber</h2>
        <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
          {status?.jobs?.map(job => {
            const info = jobDescriptions[job.id] || { name: job.name, desc: '', schedule: job.trigger };
            const nextRun = job.next_run ? new Date(job.next_run).toLocaleString('nb-NO') : 'Ikke planlagt';
            
            return (
              <div key={job.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-white">{info.name}</div>
                    <div className="text-sm text-gray-400 mt-1">{info.desc}</div>
                    <div className="text-xs text-gray-500 mt-2 flex items-center gap-4">
                      <span>📅 {info.schedule}</span>
                      <span>⏭️ Neste: {nextRun}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => runJob(job.id)}
                    disabled={runningJob === job.id}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {runningJob === job.id ? 'Kjører...' : 'Kjør nå'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info box */}
      <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400">
        <p><strong>Tips:</strong> E-postjobbene kjøres automatisk etter planen. Du kan også kjøre dem manuelt ved å klikke "Kjør nå".</p>
        <p className="mt-2">E-poster sendes kun til aktive brukere med Admin- eller PowerUser-rolle.</p>
      </div>
    </div>
  );
}
